import React from 'react';
import { ArrowRight, CheckCircle2, BarChart3, Rocket, Users, Brain } from 'lucide-react';
import { useLanguage } from './contexts/LanguageContext';
import { LanguageSelector } from './components/LanguageSelector';
import { EditableContent } from './components/EditableContent';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminBar } from './components/AdminBar';
import { SolutionPage } from './pages/SolutionPage';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { AiBoosterSprint } from './pages/AiBoosterSprint';
import { ToolForm } from './pages/ToolForm';
import { BlogForm } from './pages/BlogForm';
import { AiBootcamp } from './pages/AiBootcamp';
import { AiTools } from './pages/AiTools';
import AiToolDetail from './pages/AiToolDetail';
import { LegalNotice } from './pages/LegalNotice';
import { TermsOfSale } from './pages/TermsOfSale';
import { RoiCalculatorPage } from './pages/RoiCalculator';
import { Footer } from './components/Footer';
import { AiQuiz } from './pages/AiQuiz';
import { AffiliateProgram } from './pages/AffiliateProgram';
import { AuditAutomation } from './pages/AuditAutomation';
import { useEffect } from 'react';
import { useContent } from './hooks/useContent';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { useTitle } from './hooks/useTitle';
import { Header } from './components/Header';

const solutions = [
  {
    icon: Users,
  },
  {
    icon: BarChart3,
  },
  {
    icon: Brain,
  },
];

const experts = [
  {
    name: 'Quentin Tousart',
    points: [
      'Expert en automatisation des ventes et marketing',
      '+2500 connexions business',
      'Spécialiste growth hacking',
      'Consultant en transformation digitale',
    ],
    image: 'https://media.licdn.com/dms/image/v2/D4E03AQEZXx_XgYGeNQ/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1724314192598?e=1743033600&v=beta&t=aEJJvyZGvjQ1n-sKdr4h-JYAVSLRY5JREYurKzM_B5U',
  },
  {
    name: 'Serge Brackman',
    points: [
      'Architecte de solutions robustes',
      'Spécialiste LowCode/NoCode',
      'Expert en intégration',
      '+15 ans d\'expérience',
    ],
    image: 'https://media.licdn.com/dms/image/v2/D4E03AQF-JF632IBg7A/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1728304737310?e=1743033600&v=beta&t=n3mv-CPLsT6gi0w1d1aHGVlRlDbs6pmJlIzUhWa3mDo',
  },
];

function App() {
  const { t } = useLanguage();
  const { fetchContent } = useContent();
  const { setTitle } = useTitle();
  
  useEffect(() => {
    fetchContent('home');
  }, [fetchContent]);

  const HomePage = () => {
    useEffect(() => {
      setTitle(t.hero.title);
    }, [setTitle, t.hero.title]);
    
    return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />
      {/* Hero Section */}
      <main id="main-content" className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <div className="flex justify-center mb-8">
            <img
              src="https://www.novster.fr/wp-content/uploads/2018/11/LogoNovster1000x250.jpg" 
              alt="Novster" 
              width="1000"
              height="250"
              className="h-24 object-contain animate-scaleIn" 
            />
          </div>
          <EditableContent
            pageId="home"
            contentKey="hero.title"
            type="text"
            defaultContent={t.hero.title}
            className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-6 animate-slideUp"
            tag="h1"
          />
          <EditableContent
            pageId="home"
            contentKey="hero.subtitle"
            type="text"
            defaultContent={t.hero.subtitle}
            className="text-xl text-center text-gray-600 max-w-2xl mx-auto mb-12 animate-slideUp"
            style={{ '--delay': '0.2s' } as React.CSSProperties}
            tag="p"
          />
          <div className="flex justify-center">
            <a
              href="https://tidycal.com/quentin.tousart/novster-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 transform hover:scale-105 transition-all animate-slideUp animate-float"
              aria-label="Réserver ma session gratuite"
              style={{ '--delay': '0.4s' } as React.CSSProperties}
            >
              <span>Réserver ma session gratuite</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </main>

      {/* Experts Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <EditableContent
            pageId="home"
            contentKey="experts.title"
            type="text"
            defaultContent={t.experts.title}
            className="text-3xl font-bold text-center mb-16"
            tag="h2"
          />
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {experts.map((expert, index) => (
              <div key={expert.name} 
                   className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all transform hover:-translate-y-1"
                   style={{ '--delay': `${0.2 + index * 0.2}s` } as React.CSSProperties}>
                <img
                  src={expert.image}
                  alt={expert.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="text-xl font-bold text-center mb-2">{expert.name}</h3>
                <EditableContent
                  pageId="home"
                  contentKey={`experts.roles.${index === 0 ? 'strategy' : 'technical'}`}
                  type="text"
                  defaultContent={index === 0 ? t.experts.roles.strategy : t.experts.roles.technical}
                  className="text-orange-500 font-semibold text-center mb-6"
                  tag="p"
                />
                <ul className="space-y-3">
                  {expert.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <EditableContent
            pageId="home"
            contentKey="solutions.title"
            type="text"
            defaultContent={t.solutions.title}
            className="text-3xl font-bold text-center mb-16"
            tag="h2"
          />
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[t.solutions.sales, t.solutions.marketing, t.solutions.bi].map((solution, index) => (
              <Link 
                key={solution.title}
                to={`/${Object.keys(t.solutions)[index]}`}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all group animate-slideUp transform hover:-translate-y-1"
                style={{ '--delay': `${0.2 + index * 0.2}s` } as React.CSSProperties}
              >
                {React.createElement(solutions[index].icon, { 
                  className: "w-12 h-12 text-orange-500 mb-6 transform transition-transform group-hover:scale-110" 
                })}
                <EditableContent
                  pageId="home"
                  contentKey={`solutions.${Object.keys(t.solutions)[index]}.title`}
                  type="text"
                  defaultContent={solution.title}
                  className="text-xl font-bold mb-4"
                  tag="h3"
                />
                <ul className="space-y-3 mb-6">
                  {solution.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>{item}</span> {/* Note: list items will be managed through the admin interface */}
                    </li>
                  ))}
                </ul>
                <EditableContent
                  pageId="home"
                  contentKey={`solutions.${Object.keys(t.solutions)[index]}.highlight`}
                  type="text"
                  defaultContent={solution.highlight}
                  className="text-orange-500 font-semibold"
                  tag="p"
                />
                <div className="mt-4 text-orange-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  En savoir plus →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Method Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <EditableContent
            pageId="home"
            contentKey="method.title"
            type="text"
            defaultContent={t.method.title}
            className="text-3xl font-bold text-center mb-16"
            tag="h2"
          />
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center animate-slideUp hover:transform hover:scale-105 transition-all"
                 style={{ '--delay': '0.2s' } as React.CSSProperties}>
              <Rocket className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-float" />
              <EditableContent
                pageId="home"
                contentKey="method.audit.title"
                type="text"
                defaultContent={t.method.audit.title}
                className="text-xl font-bold mb-2"
                tag="h3"
              />
              <EditableContent
                pageId="home"
                contentKey="method.audit.description"
                type="text"
                defaultContent={t.method.audit.description}
                className="text-gray-600"
                tag="p"
              />
            </div>
            <div className="text-center animate-slideUp hover:transform hover:scale-105 transition-all"
                 style={{ '--delay': '0.4s' } as React.CSSProperties}>
              <Brain className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-float" />
              <EditableContent
                pageId="home"
                contentKey="method.implementation.title"
                type="text"
                defaultContent={t.method.implementation.title}
                className="text-xl font-bold mb-2"
                tag="h3"
              />
              <EditableContent
                pageId="home"
                contentKey="method.implementation.description"
                type="text"
                defaultContent={t.method.implementation.description}
                className="text-gray-600"
                tag="p"
              />
            </div>
            <div className="text-center animate-slideUp hover:transform hover:scale-105 transition-all"
                 style={{ '--delay': '0.6s' } as React.CSSProperties}>
              <Users className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-float" />
              <EditableContent
                pageId="home"
                contentKey="method.support.title"
                type="text"
                defaultContent={t.method.support.title}
                className="text-xl font-bold mb-2"
                tag="h3"
              />
              <EditableContent
                pageId="home"
                contentKey="method.support.description"
                type="text"
                defaultContent={t.method.support.description}
                className="text-gray-600"
                tag="p"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Ils ont transformé leur business avec l'IA</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                company: "TechVision",
                result: "+45% de productivité",
                description: "Automatisation complète du support client et de la qualification des leads",
                logo: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=300"
              },
              {
                company: "DataFlow",
                result: "2.3M€ de CA additionnel",
                description: "Nouveaux services basés sur l'IA et optimisation des processus internes", 
                logo: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=300"
              },
              {
                company: "SmartRetail",
                result: "-30% de coûts opérationnels",
                description: "Prédiction des stocks et personnalisation client en temps réel",
                logo: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300"
              }
            ].map((story, index) => (
              <div key={story.company}
                   className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all transform hover:-translate-y-1"
                   style={{ '--delay': `${0.2 * index}s` } as React.CSSProperties}>
                <img
                  src={story.logo}
                  alt={story.company}
                  className="w-24 h-24 object-cover rounded-lg mb-6"
                />
                <h3 className="text-xl font-bold mb-2">{story.company}</h3>
                <p className="text-2xl font-bold text-orange-500 mb-4">{story.result}</p>
                <p className="text-gray-600">{story.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <EditableContent
            pageId="home"
            contentKey="cta.title"
            type="text"
            defaultContent={t.cta.title}
            className="text-3xl font-bold mb-8"
            tag="h2"
          />
          <EditableContent
            pageId="home"
            contentKey="cta.subtitle"
            type="text"
            defaultContent={t.cta.subtitle}
            className="text-xl mb-12"
            tag="p"
          />
          <a
            href="https://tidycal.com/quentin.tousart/novster-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-orange-500 px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-gray-100 transform hover:scale-105 transition-all"
          >
            <EditableContent
              pageId="home"
              contentKey="cta.button"
              type="text"
              defaultContent={t.cta.button}
              className="inline"
              tag="span"
            />
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  )};

  return (
    <Router>
      <Header />
      <a href="#main-content" className="skip-to-content">
        Aller au contenu principal
      </a>
      <Routes>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/:slug" element={<SolutionPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        <Route path="/ai-booster-sprint" element={<AiBoosterSprint />} />
        <Route path="/admin/tools/new" element={<ToolForm />} />
        <Route path="/admin/tools/edit/:id" element={<ToolForm />} />
        <Route path="/admin/blog/new" element={<BlogForm />} />
        <Route path="/admin/blog/edit/:id" element={<BlogForm />} />
        <Route path="/bootcamp" element={<AiBootcamp />} />
        <Route path="/tools" element={<AiTools />} />
        <Route path="/tools/:id" element={<AiToolDetail />} />
        <Route path="/quiz" element={<AiQuiz />} />
        <Route path="/mentions-legales" element={<LegalNotice />} />
        <Route path="/calculateur-roi" element={<RoiCalculatorPage />} />
        <Route path="/cgv" element={<TermsOfSale />} />
        <Route path="/recommandation" element={<AffiliateProgram />} />
        <Route path="/audit" element={<AuditAutomation />} />
        <Route path="/" element={<HomePage />} />
      </Routes>
      <Footer />
    </Router>
  );
}

export default App;